import type {
  Currency,
  Language,
  Region,
} from '@whoop/i18n/types/internationalization';
import type { JoinFlowContent } from 'services/generated/growth-content-service/models/JoinFlowContent';
import type { ApplicationScheme } from 'services/generated/growth-content-service/models/ApplicationScheme';
import type { CheckoutPromo } from 'services/generated/membership-service-v2/models/CheckoutPromo';
import {
  getFirstVariant,
  getMembershipTypeApplicationSchemeForCurrency,
  getPromoDisclaimer,
  getReferrer,
} from 'ui';
import type { Product } from 'services/generated/commerce-service/models/Product';
import { bandMaterialSorter } from 'ui/utils/variantHelpers';
import { MembershipTier } from '@/lib/constants/maverick';
import { findDefaultBand } from '@/lib/utils/maverickHelpers';

export const getLandingPageSubtextCopyV2 = ({
  promoContent,
  promoInfo,
  currency,
  language,
  region,
  showAdvancedLabsCopy = false,
  isRafGiftFlow = false,
}: {
  promoContent?: JoinFlowContent;
  promoInfo?: CheckoutPromo;
  currency: Currency;
  language: Language;
  region: Region;
  showAdvancedLabsCopy: boolean;
  isRafGiftFlow?: boolean;
}):
  | {
      secondLine?: string;
      mobileSubtext: string;
      desktopSubtext: string;
      imageSrc: string | undefined;
    }
  | undefined => {
  // If partnerships/rafGift promo content has been populated, use that.
  // Eventually, that will contain the default text and RAF/Affiliate text as well
  if (promoContent && (promoContent.partnership_name || isRafGiftFlow)) {
    return {
      imageSrc: promoContent.content.image,
      desktopSubtext: promoContent.content.base_desktop_subtext,
      mobileSubtext: promoContent.content.base_mobile_subtext,
      secondLine: undefined,
    };
  }

  const referrer = getReferrer(promoInfo);
  if (referrer && promoInfo) {
    const validPromo = getMembershipTypeApplicationSchemeForCurrency(
      promoInfo.promo_code.application_schemes as ApplicationScheme[],
      currency,
    );
    const referrerText = getPromoDisclaimer({
      promo: validPromo,
      location: 'primary',
      referrer,
      currency,
      language,
      region,
      showAdvancedLabsCopy,
    });
    return {
      imageSrc: referrer.avatar_url,
      desktopSubtext: referrerText,
      mobileSubtext: referrerText,
      secondLine: undefined,
    };
  }

  return undefined;
};

export const sortAndFilterLandingSwatchesV2 = ({
  products,
  tier,
}: {
  products: Product[];
  tier: MembershipTier;
}): Product[] => {
  const defaultProduct = findDefaultBand({ products, tier });
  if (!defaultProduct) return products;
  const defaultVariant = getFirstVariant(defaultProduct);
  return products.sort((product1, product2) => {
    const variant1 = getFirstVariant(product1);
    const variant2 = getFirstVariant(product2);
    // Sort the default product first
    if (variant1.sku === defaultVariant.sku) return -1;
    if (variant2.sku === defaultVariant.sku) return 1;
    return bandMaterialSorter(variant1, variant2);
  });
};

export const getLandingPageSecondaryCTAButtonTextKey = ({
  skipGiftSelection,
}: {
  skipGiftSelection: boolean;
}): string => {
  let textKey = 'buyAsGift';

  if (skipGiftSelection) {
    textKey = 'buyForYourself';
  }

  return textKey;
};
